/**
 * ToolWindowContent - Renders the real ToolDetailPage inside a tiled window.
 *
 * Wraps ToolDetailPage in CapabilityDetailProvider (which the route-level
 * CapabilityDetailPage normally provides) and passes appKey/name as props
 * so the page works without route params.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCapabilityDetail as useCapabilityDetailData } from '@/hooks/use-entity-detail';
import { CapabilityDetailProvider } from '@/lib/capability-detail-context';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import { Archive, Pencil } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ToolDetailPage from '@/pages/tools/tool-detail-page';
import { useTiledWindows } from '@/components/tiled-windows';
import type { ToolWindow } from '@/types/tiled-window';

interface ToolWindowContentProps {
    window: ToolWindow;
}

function parseEntityRef(entityId: string | number, fallbackAppKey: string): { appKey: string; name: string } {
    const raw = String(entityId).replace(/::.*$/, '');
    if (raw.includes('/')) {
        const slashIdx = raw.indexOf('/');
        return { appKey: raw.substring(0, slashIdx), name: raw.substring(slashIdx + 1) };
    }
    return { appKey: fallbackAppKey, name: raw };
}

function ToolWindowInner({ window: win }: ToolWindowContentProps) {
    const parsed = parseEntityRef(win.entityId, win.appKey ?? '');
    const appKey = parsed.appKey;
    const name = parsed.name || win.entityName;
    const headerLayerId = `window-${win.id}`;
    const { data, refetch } = useCapabilityDetailData(appKey, name);
    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();

    const actions = useCapabilityActions({
        appKey,
        name,
        typeLabel: 'Tool',
        data: data as Record<string, unknown> | null,
        refetch,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    useEffect(() => {
        if (!data || actions.isArchived) {
            removeHeaderLayer(headerLayerId);
            return;
        }
        pushHeaderLayer(headerLayerId, {
            action: (
                <CapabilityHeaderActions
                    typeLabel="Tool"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={actions.isArchived}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(headerLayerId);
    }, [
        data,
        headerLayerId,
        actions.hasWorkingRevision,
        actions.isViewingPublished,
        actions.isArchived,
        actions.selectedPublishedLabel,
        actions.isDirty,
        actions.isSaving,
        actions.isPublishing,
        pushHeaderLayer,
        removeHeaderLayer,
    ]);

    return (
        <>
            <div className="h-full w-full overflow-auto">
                <ToolDetailPage appKey={appKey} name={name} headerLayerId={headerLayerId} />
            </div>
            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export function ToolWindowContent({ window: win }: ToolWindowContentProps) {
    const parsed = parseEntityRef(win.entityId, win.appKey ?? '');
    const appKey = parsed.appKey;
    const name = parsed.name || win.entityName;
    const queryClient = useQueryClient();
    const { closeWindow } = useTiledWindows();

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const openArchiveDialog = useCallback(() => setArchiveDialogOpen(true), []);

    const handleArchive = useCallback(async () => {
        if (!appKey || !name) return;
        setIsArchiving(true);
        try {
            await api.delete(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`);
            toast.success('Tool archived successfully');
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            closeWindow(win.id);
        } catch {
            toast.error('Failed to archive tool');
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }, [appKey, name, queryClient, closeWindow, win.id]);

    return (
        <CapabilityDetailProvider openArchiveDialog={openArchiveDialog}>
            <ToolWindowInner window={win} />

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive Tool
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{name}&quot;? It will no longer
                            appear in the active tool list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </CapabilityDetailProvider>
    );
}

